import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../../store';
import { selectDisco } from '../../../store/selectors/databaseSelectors';
import { addDisco } from '../../../store/slices/databaseSlice';
import { discoService, discoDetailsService, DiscoDetail } from '../../../services';
import { Disco, DiscoType } from '../../../types';
import { TOAST_CONFIG, TOAST_WARNING_CONFIG } from '../../../constants';
import { SearchDropdown } from '../../../components';
import toast from 'react-hot-toast';

interface DiscoForm {
  modelNumber: string;
  brand: string;
  discoRating: string;
  type: DiscoType;
  fuseRatings: string;
}

// We'll get brands dynamically from Redux

// Type options for dropdown
const DISCO_TYPES = [
  { id: DiscoType.FUSED, label: 'Fused' },
  { id: DiscoType.NON_FUSED, label: 'Non-Fused' }
];

export const DiscoManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  
  // Get data from Redux
  const discoSwitches = useAppSelector(selectDisco);
  
  // Tab state
  const [activeTab, setActiveTab] = useState<'switches' | 'details'>('switches');
  const [activeDetailsTab, setActiveDetailsTab] = useState<'text' | 'location'>('text');
  
  // Form states
  const [discoForm, setDiscoForm] = useState<DiscoForm>({
    modelNumber: '',
    brand: '',
    discoRating: '',
    type: DiscoType.FUSED,
    fuseRatings: ''
  });
  
  // Disco Details form states
  const [detailsForm, setDetailsForm] = useState<{type: 'text' | 'location', value: string}>({
    type: 'text',
    value: ''
  });
  const [selectedDetailTypeOption, setSelectedDetailTypeOption] = useState<{id: string, label: string}>({ id: 'text', label: 'Text' });
  const [discoDetails, setDiscoDetails] = useState<DiscoDetail[]>([]);
  const [isSubmittingDetail, setIsSubmittingDetail] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  
  // UI states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localDiscos, setLocalDiscos] = useState<Disco[]>(discoSwitches);
  const [selectedBrandOption, setSelectedBrandOption] = useState<{id: string, label: string} | null>(null);
  const [selectedTypeOption, setSelectedTypeOption] = useState<{id: string, label: string} | null>(null);
  const [isAddingNewBrand, setIsAddingNewBrand] = useState(false);
  const [newBrandName, setNewBrandName] = useState('');

  // Initialize local state with Redux data
  useEffect(() => {
    setLocalDiscos(discoSwitches);
  }, [discoSwitches]);

  // Get unique brand names from existing disco switches
  const existingBrands = React.useMemo(() => {
    const brandSet = new Set(localDiscos.map(disco => disco.brand));
    return Array.from(brandSet).sort();
  }, [localDiscos]);

  // Create brand options with "Add New" option
  const brandOptions = [
    ...existingBrands.map(brand => ({ id: brand, label: brand })),
    { id: '__add_new__', label: '+ Add New Brand' }
  ];

  // Handle brand selection
  const handleBrandSelect = (option: {id: string, label: string} | null) => {
    if (option?.id === '__add_new__') {
      setIsAddingNewBrand(true);
      setSelectedBrandOption(null);
    } else {
      setSelectedBrandOption(option);
      setIsAddingNewBrand(false);
      setDiscoForm(prev => ({ ...prev, brand: option?.label || '' }));
    }
  };

  // Handle type selection
  const handleTypeSelect = (option: {id: string, label: string} | null) => {
    setSelectedTypeOption(option);
    setDiscoForm(prev => ({ ...prev, type: (option?.id as DiscoType) || DiscoType.FUSED }));
  };

  // Handle new brand submission
  const handleNewBrandSubmit = () => {
    if (newBrandName.trim()) {
      const newOption = { id: newBrandName.trim(), label: newBrandName.trim() };
      setSelectedBrandOption(newOption);
      setDiscoForm(prev => ({ ...prev, brand: newBrandName.trim() }));
      setIsAddingNewBrand(false);
      setNewBrandName('');
    }
  };

  // Cancel new brand addition
  const handleCancelNewBrand = () => {
    setIsAddingNewBrand(false);
    setNewBrandName('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!discoForm.modelNumber.trim() || !discoForm.brand.trim() || !discoForm.discoRating.trim()) {
      toast('Please fill in all required fields', TOAST_WARNING_CONFIG);
      return;
    }

    const rating = parseInt(discoForm.discoRating.trim());
    if (isNaN(rating) || rating <= 0) {
      toast('Please enter a valid disco rating', TOAST_WARNING_CONFIG);
      return;
    }

    // Parse fuse ratings
    let fuseRatingsArray: number[] = [];
    if (discoForm.fuseRatings.trim()) {
      try {
        fuseRatingsArray = discoForm.fuseRatings
          .split(',')
          .map(rating => {
            const parsed = parseInt(rating.trim());
            if (isNaN(parsed) || parsed <= 0) {
              throw new Error('Invalid fuse rating');
            }
            return parsed;
          });
      } catch (error) {
        toast('Please enter valid fuse ratings (comma-separated numbers)', TOAST_WARNING_CONFIG);
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const newDisco = await discoService.createDisco({
        modelNumber: discoForm.modelNumber.trim(),
        brand: discoForm.brand.trim(),
        discoRating: rating,
        type: discoForm.type,
        fuseRatings: fuseRatingsArray
      });
      
      // Update Redux state
      dispatch(addDisco(newDisco));
      
      // Update local state - add new disco at the beginning
      setLocalDiscos(prev => [newDisco, ...prev]);
      
      // Reset form
      setDiscoForm({
        modelNumber: '',
        brand: '',
        discoRating: '',
        type: DiscoType.FUSED,
        fuseRatings: ''
      });
      setSelectedBrandOption(null);
      setSelectedTypeOption(null);
      setIsAddingNewBrand(false);
      setNewBrandName('');
      
      toast.success('Disconnect switch created successfully!', TOAST_CONFIG.toastOptions.success);
    } catch (error) {
      console.error('Error creating disconnect switch:', error);
      toast.error('Failed to create disconnect switch. Please try again.', TOAST_CONFIG.toastOptions.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle disco detail type selection
  const handleDetailTypeSelect = (option: any) => {
    if (option) {
      setSelectedDetailTypeOption(option);
      setDetailsForm(prev => ({ ...prev, type: option.id as 'text' | 'location' }));
    }
  };

  // Handle disco detail submission
  const handleDetailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmittingDetail(true);

    try {
      const newDetail = await discoDetailsService.create({
        type: detailsForm.type,
        value: detailsForm.value.trim()
      });
      
      // Add to local state
      setDiscoDetails([...discoDetails, newDetail]);
      
      // Reset form
      setDetailsForm({ type: 'text', value: '' });
      setSelectedDetailTypeOption({ id: 'text', label: 'Text' });
      
      toast.success('Disco detail added successfully!', TOAST_CONFIG.toastOptions.success);
    } catch (error) {
      console.error('Error creating disco detail:', error);
      toast.error('Failed to add disco detail. Please try again.', TOAST_CONFIG.toastOptions.error);
    } finally {
      setIsSubmittingDetail(false);
    }
  };


  // Fetch disco details on mount
  useEffect(() => {
    const fetchDetails = async () => {
      setIsLoadingDetails(true);
      try {
        const details = await discoDetailsService.getAll();
        setDiscoDetails(details);
      } catch (error) {
        console.error('Error fetching disco details:', error);
        toast.error('Failed to load disco details.', TOAST_CONFIG.toastOptions.error);
      } finally {
        setIsLoadingDetails(false);
      }
    };
    
    if (activeTab === 'details') {
      fetchDetails();
    }
  }, [activeTab]);

  return (
    <div className="p-4">
      {/* Tab Navigation */}
      <div className="mb-4">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('switches')}
              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                activeTab === 'switches'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Disconnect Switches
            </button>
            <button
              onClick={() => setActiveTab('details')}
              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                activeTab === 'details'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Disco Details
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'switches' ? (
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Form Section */}
          <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-3">
            Add New Disconnect Switch
          </h3>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Model Number *
              </label>
              <input
                type="text"
                value={discoForm.modelNumber}
                onChange={(e) => setDiscoForm(prev => ({ ...prev, modelNumber: e.target.value }))}
                placeholder="e.g., H361"
                className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Brand *
              </label>
              {isAddingNewBrand ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={newBrandName}
                    onChange={(e) => setNewBrandName(e.target.value)}
                    placeholder="Enter new brand name"
                    className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleNewBrandSubmit}
                      disabled={!newBrandName.trim()}
                      className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Add
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelNewBrand}
                      className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white text-xs font-medium rounded"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <SearchDropdown
                  options={brandOptions}
                  onSelect={handleBrandSelect}
                  placeholder="Select brand..."
                  value={selectedBrandOption}
                  className="text-sm"
                  inputClassName="px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  zIndex={1000}
                />
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Disco Rating *
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="1"
                  value={discoForm.discoRating}
                  onChange={(e) => setDiscoForm(prev => ({ ...prev, discoRating: e.target.value }))}
                  placeholder="e.g., 60"
                  className="w-full px-3 py-2 pr-8 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-slate-500">A</span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Type *
              </label>
              <SearchDropdown
                options={DISCO_TYPES}
                onSelect={handleTypeSelect}
                placeholder="Select type..."
                value={selectedTypeOption}
                className="text-sm"
                inputClassName="px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                zIndex={999}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Fuse Ratings {discoForm.type === DiscoType.FUSED ? '*' : '(Optional)'}
              </label>
              <input
                type="text"
                value={discoForm.fuseRatings}
                onChange={(e) => setDiscoForm(prev => ({ ...prev, fuseRatings: e.target.value }))}
                placeholder={discoForm.type === DiscoType.FUSED ? "e.g., 30, 60, 100" : "Only available for FUSED type"}
                className={`w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                  discoForm.type !== DiscoType.FUSED ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : ''
                }`}
                disabled={discoForm.type !== DiscoType.FUSED}
                required={discoForm.type === DiscoType.FUSED}
              />
              <p className="text-xs text-slate-500 mt-1">
                {discoForm.type === DiscoType.FUSED 
                  ? "Enter comma-separated amperage values (e.g., 30, 60, 100)"
                  : "Fuse ratings are only applicable for FUSED disconnect switches"
                }
              </p>
            </div>
            
            <button
              type="submit"
              disabled={isSubmitting || !discoForm.modelNumber.trim() || !discoForm.brand.trim() || !discoForm.discoRating.trim() || !selectedTypeOption || (discoForm.type === DiscoType.FUSED && !discoForm.fuseRatings.trim())}
              className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isSubmitting ? 'Creating...' : 'Create Disconnect Switch'}
            </button>
          </form>
        </div>

        {/* List Section */}
        <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-slate-900">
              Existing Disconnect Switches
            </h3>
            <span className="text-xs text-slate-500">
              {localDiscos.length} items
            </span>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {localDiscos.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-1.5 px-2 font-medium text-slate-700">Brand</th>
                      <th className="text-left py-1.5 px-2 font-medium text-slate-700">Model Number</th>
                      <th className="text-left py-1.5 px-2 font-medium text-slate-700">Disco Rating</th>
                      <th className="text-left py-1.5 px-2 font-medium text-slate-700">Type</th>
                      <th className="text-left py-1.5 px-2 font-medium text-slate-700">Fuses</th>
                    </tr>
                  </thead>
                  <tbody>
                    {localDiscos.map((disco) => (
                      <tr key={disco.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-1.5 px-2 text-slate-800">{disco.brand}</td>
                        <td className="py-1.5 px-2 text-slate-800 font-medium">{disco.modelNumber}</td>
                        <td className="py-1.5 px-2 text-slate-800">{disco.discoRating}A</td>
                        <td className="py-1.5 px-2">
                          <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded-full ${
                            disco.type === DiscoType.FUSED 
                              ? 'bg-orange-100 text-orange-800' 
                              : 'bg-slate-100 text-slate-800'
                          }`}>
                            {disco.type}
                          </span>
                        </td>
                        <td className="py-1.5 px-2 text-slate-600">
                          {disco.fuseRatings.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {disco.fuseRatings.map((rating, index) => (
                                <span key={index} className="text-[10px] bg-orange-50 text-orange-700 px-1 py-0.5 rounded border border-orange-200">
                                  {rating}A
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-slate-500 text-xs text-center py-8">No disconnect switches found</p>
            )}
          </div>
        </div>
      </div>
      ) : (
        /* Disco Details Tab */
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Form Section */}
          <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
            <h3 className="text-base font-semibold text-slate-900 mb-3">
              Add New Disco Detail
            </h3>

            <form onSubmit={handleDetailSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Type *
                </label>
                <SearchDropdown
                  options={[
                    { id: 'text', label: 'Text' },
                    { id: 'location', label: 'Location' }
                  ]}
                  onSelect={handleDetailTypeSelect}
                  placeholder="Select type..."
                  value={selectedDetailTypeOption}
                  className="text-sm"
                  inputClassName="px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  zIndex={999}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Value *
                </label>
                <input
                  type="text"
                  value={detailsForm.value}
                  onChange={(e) => setDetailsForm(prev => ({ ...prev, value: e.target.value }))}
                  placeholder={detailsForm.type === 'text' ? 'e.g., AC DISCONNECT' : 'e.g., NORTH OUTSIDE WALL'}
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
                <p className="text-xs text-slate-500 mt-1">
                  {detailsForm.type === 'text' 
                    ? 'Enter a descriptive text for the disconnect switch'
                    : 'Enter a location description'
                  }
                </p>
              </div>
              
              <button
                type="submit"
                disabled={isSubmittingDetail || !detailsForm.value.trim()}
                className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {isSubmittingDetail ? 'Adding...' : 'Add Disco Detail'}
              </button>
            </form>
          </div>

          {/* List Section with Tabs */}
          <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-slate-900">
                Existing Disco Details
              </h3>
              <span className="text-xs text-slate-500">
                {discoDetails.length} items
              </span>
            </div>

            {/* Tab Navigation */}
            <div className="mb-4">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                  <button
                    onClick={() => setActiveDetailsTab('text')}
                    className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                      activeDetailsTab === 'text'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Text Options ({discoDetails.filter(detail => detail.type === 'text').length})
                  </button>
                  <button
                    onClick={() => setActiveDetailsTab('location')}
                    className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                      activeDetailsTab === 'location'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Location Options ({discoDetails.filter(detail => detail.type === 'location').length})
                  </button>
                </nav>
              </div>
            </div>

            {/* Tab Content */}
            <div className="max-h-80 overflow-y-auto">
              {isLoadingDetails ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-slate-500 text-sm mt-2">Loading disco details...</p>
                </div>
              ) : activeDetailsTab === 'text' ? (
                <div className="space-y-1">
                  {discoDetails
                    .filter(detail => detail.type === 'text')
                    .map((detail) => (
                      <div key={detail.id} className="px-2 py-1 bg-blue-50 text-blue-800 text-xs rounded border border-blue-200">
                        <span className="font-medium">{detail.value}</span>
                      </div>
                    ))
                  }
                  {discoDetails.filter(detail => detail.type === 'text').length === 0 && (
                    <div className="text-center py-8">
                      <div className="text-gray-400 mb-2">
                        <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <p className="text-slate-500 text-sm">No text options found</p>
                      <p className="text-slate-400 text-xs">Add your first text option using the form</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-1">
                  {discoDetails
                    .filter(detail => detail.type === 'location')
                    .map((detail) => (
                      <div key={detail.id} className="px-2 py-1 bg-green-50 text-green-800 text-xs rounded border border-green-200">
                        <span className="font-medium">{detail.value}</span>
                      </div>
                    ))
                  }
                  {discoDetails.filter(detail => detail.type === 'location').length === 0 && (
                    <div className="text-center py-8">
                      <div className="text-gray-400 mb-2">
                        <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        </svg>
                      </div>
                      <p className="text-slate-500 text-sm">No location options found</p>
                      <p className="text-slate-400 text-xs">Add your first location option using the form</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};